import { useMemo } from 'react';

import { Box, Text, Tooltip } from '@mantine/core';
import { components, OptionProps } from 'react-select';

import { useGetActiveAppointmentsQuery } from '@/store/slices/api/appointmentApiSlice';
import formatDate from '@/utils/formatDate';

import { LabelAndValue } from '@/types/custom';
import { Appointment } from '@/types/matchDevice';
import Dropdown from '@/ui/Dropdown';

type AppointmentOption = LabelAndValue & {
  isDisabled?: boolean;
  tooltip?: string;
};

const CustomOption = (props: OptionProps<AppointmentOption, false>) => {
  const { data, children, ...rest } = props;

  if (data.isDisabled) {
    return (
      <Tooltip
        position="right-end"
        zIndex={1000}
        label={data.tooltip || 'This appointment already has a transmission associated with it'}>
        <Box>
          <components.Option {...rest} data={data} isDisabled={true}>
            <Text c="dimmed" fz="sm" td="line-through" fw={500}>
              {children}
            </Text>
          </components.Option>
        </Box>
      </Tooltip>
    );
  }

  return <components.Option {...props} />;
};

export const AppointmentSelection = ({
  deviceId,
  appointment,
  handleChange,
  isDisabled
}: {
  reportId: number;
  deviceId: string;
  appointment: Appointment | null;
  isDisabled: boolean;
  handleChange: (option: LabelAndValue | null) => void;
}) => {
  const { data: appointments, isLoading } = useGetActiveAppointmentsQuery(
    { deviceId },
    {
      skip: isDisabled
    }
  );

  const getAppointmentName = (appointment: Appointment) => {
    return `${appointment.type} - ${formatDate(appointment.scheduledDate)} (${appointment.status})`;
  };

  const options = useMemo((): AppointmentOption[] => {
    if (!appointments || isLoading) return [];

    const appointmentOptions = appointments.map((appointment) => ({
      value: appointment.id,
      label: getAppointmentName(appointment),
      isDisabled: !!appointment.linkedTransmissionReport,
      tooltip: appointment.linkedTransmissionReport
        ? `This appointment already has transmission report #${appointment.linkedTransmissionReport.id} associated with it`
        : undefined
    }));

    return [{ value: '', label: '-' }, ...appointmentOptions];
  }, [appointments, isLoading]);

  const selectedAppointment = useMemo(() => {
    if (isDisabled && appointment) {
      return {
        value: appointment.id,
        label: getAppointmentName(appointment)
      };
    }
    return options.find((option) => option.value === appointment?.id) || { value: '', label: '-' };
  }, [options, appointment, isDisabled]);

  const handleAppointmentChange = (option: AppointmentOption | null) => {
    if (option?.value === '') {
      handleChange(null);
    } else {
      handleChange(option);
    }
  };

  return (
    <Dropdown
      options={options}
      onChange={handleAppointmentChange}
      isMulti={false}
      value={selectedAppointment}
      placeholder={isLoading ? 'Loading appointments...' : 'Select an appointment'}
      isDisabled={isLoading || isDisabled}
      customComponents={{ Option: CustomOption }}
    />
  );
};
